import React, { useState, useEffect, useRef } from "react"
import { navigate } from "gatsby"
import Flink from '~/components/shared/flink'
import { useTheme } from '~/contexts/ThemeContext'
import { useCart } from '~/contexts/CartContext'
import { imgSize, deShopId, reShopId, jnstyls } from '~/utils/helpers'
import useCartHook from '~/hooks/useCartHook'
import useAnalytics from '~/utils/analytics'
import useTestFunnelHook from '~/utils/test-funnel'

import IconClose from '~/components/icons/close'
import IconCart from '~/components/icons/cart'
import CartItem from '~/components/cart/cart-item'
import CartUpsell from '~/components/cart/upsell'
import PlaceholdAfterp from '~/components/cart/placeholdafterp'

import { aria_btn, cta_link, solid_btn, bg } from "~/styles/app.module.scss"
import { cart_qty } from "~/styles/header.module.scss"
import * as styles from "~/styles/panels/cart.module.scss"


const Cart = () => {
  const { freeze, setFreeze } = useTheme()
  const { 
    cart, setCartOpen, cartClose, setCartClose, cartChanging, setCartChanging, 
    cartError, setCartError, showGiftCard
  } = useCart()

  const { elev_cart } = useAnalytics()
  const { fnnl_check } = useTestFunnelHook()

  const [cartQty, setCartQty] = useState(0)
  const [cartLines, setCartLines] = useState([])
  const [lastProd, setLastProd] = useState(null)
  const [total, setTotal] = useState(0)
  const [freeShip, setFreeShip] = useState(false)
  const [blockKey, setBlockKey] = useState(false)

  const bgRef = useRef(null)
  const panelRef = useRef(null)

  const checkLinesQty = () => {
    let cart_lines = (cart && cart.lines?.edges?.length > 0) ? cart.lines.edges : []
    cart_lines = cart_lines.map(x => x.node)
    const cart_qty = cart_lines.length ? cart_lines.reduce((total, x) => total + x.quantity, 0) : 0
    setCartLines(cart_lines)
    setCartQty(cart_qty)
  }

  

  const after_limit = 0

  const price_format = (price) => {
    const fprice = price.includes('.') && price.includes('.0') ? parseFloat(price).toFixed(0) : parseFloat(price).toFixed(2)
    return `$${fprice}`
  }

  const handleClose = () => {
    setCartClose(true)
    setFreeze(false)
  }

  const checkTotalFreeShip = () => {
    const total_amt = cart ? parseFloat(cart.estimatedCost.subtotalAmount.amount) : 0
    setTotal(total_amt)
    let free_ship = cart && total_amt > 150

    const discnt_ship = cart?.discountCodes?.find(x => x.code === 'FIRSTSHIPPING' || x.code === 'BMFREESHIPPING')
    if (discnt_ship && discnt_ship.applicable) free_ship = true

    setFreeShip(free_ship)
  }

  useEffect(() => {
    const closeListen = (e) => {
      if (blockKey) return
      if (e.key === 'Escape') {
        window.removeEventListener('keydown', closeListen)
        handleClose()
      }
    }
    window.addEventListener('keydown', closeListen)

    return () => {
      window.removeEventListener('keydown', closeListen)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockKey, showGiftCard])

  useEffect(() => {
    if (blockKey && !showGiftCard) setBlockKey(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGiftCard, blockKey])

  useEffect(() => {
    if (cartLines.length < 1) return

    setLastProd(cartLines[0])

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartLines, cart])


  useEffect(() => {
    if (!cart) return

    checkLinesQty()
    checkTotalFreeShip()
    elev_cart()
    fnnl_check()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  useEffect(() => {
    if (cartClose === false) return
    if (!(bgRef.current && panelRef.current)) return

    bgRef.current.setAttribute('aria-hidden', 'true')
    panelRef.current.setAttribute('aria-hidden', 'true')

    setFreeze(false)
    setCartOpen(false)
    setCartClose(false)
    if (cartError) {
      setCartError(false)
      setCartChanging(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartClose, cartError, cartChanging]);

  useEffect(() => {
    if (freeze === true) return

    setFreeze(true)
    const bg_el = bgRef.current
    const panel_el = panelRef.current

    setTimeout(() => {
      bg_el.setAttribute('aria-hidden', 'false')
      panel_el.setAttribute('aria-hidden', 'false')
    }, 100)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFreeze])

  // <CartItem key={`${item.id}-${index}`} line={line} index={index} handleClose={handleClose} setBlockKey={setBlockKey} /> 

  return (
    <>
      <button 
        type="button"
        className={bg} 
        aria-hidden="true" 
        ref={bgRef} 
        onClick={(e)=> {handleClose()}}
        style={{textAlign: 'right'}}
        aria-label={'Close cart panel'}
      />
      <aside 
        className={jnstyls([styles.cart])}
        ref={panelRef}
        role="dialog" 
        aria-modal="true"
        aria-hidden="true" 
        style={{pointerEvents: cartChanging ? 'none' : ''}}
        cart=''
      >
        <button 
          type="button"
          className={styles.close}
          aria-label="Close cart" 
          onClick={(e)=> {handleClose()}}
        >
          <IconClose icon_classes={styles.icon_close} />
        </button>

        <div className={styles.head}>
          <div className={styles.head_icon}>
            <IconCart icon_classes={styles.icon_cart}></IconCart>
            {cartQty > 0 && (
              <div className={[cart_qty, styles.cart_qty].join(' ')}>{cartQty}</div>
            )}
          </div>

          {(cartQty > 0 && !freeShip) ? (
            <div className={styles.free_ship}>
              You’re ${150 - total} away from free shipping
            </div>
          ) : (cartQty > 0 && freeShip ) ? (
            <div className={styles.free_ship}>
              Your order qualifies for free U.S. shipping
            </div>
          ) : (
            <div className={styles.free_ship}>
              Free shipping on orders over $150
            </div>
          )}
          
          {cartQty > 0 && (
            <div className={styles.progress}>
              <div className={styles.prog_bar} style={{width: freeShip ? '100%' : `${(1 - (150-35)/150) * 100}%`}}></div>
            </div>
          )}
          
          {cartQty > 0 && (
            <div className={styles.countwr}>
              <div className={styles.count}>
                {cartQty}{` Item${cartQty > 1 ? 's' : ''}`}&nbsp;&mdash;&nbsp;{`$${total}`}
              </div>
              <div className={styles.returns}>
                We’ve got you &mdash; Enjoy 365 day returns
              </div>
            </div>
            
          )}
        </div>

        {cartQty > 0 ? (
          <>
            <div className={styles.body}>
              <div className={styles.items}>
                {cartLines.map((line, index) => 
                  <CartItem 
                    key={`${line.merchandise.id}-${index}`} 
                    line={line} 
                    handleClose={handleClose} 
                    setBlockKey={setBlockKey} 
                  /> 
                )}
              </div>
              <CartUpsell lastProd={lastProd} cartLines={cartLines} />
            </div>

            <div className={styles.foot}>
              {(cart) && (
                <>
                  <div className={styles.total}>
                    <div className={styles.label}>
                      Subtotal
                    </div>
                    <div className={styles.copy}>
                      {price_format(cart.estimatedCost.subtotalAmount.amount)}
                    </div>
                    <div className={styles.label}>
                      Est Shipping
                    </div>
                    <div className={styles.copy}>
                      {freeShip ? 'FREE' : '$8'}
                    </div>
                    {(cart.discountCodes.length > 0 && cart.discountCodes[0].applicable) && (
                      <>
                        <div className={styles.label}>
                          Discount<span className={styles.label_checkout}>Applied at checkout</span>
                        </div>
                        <div className={styles.copy}>
                          {cart.discountCodes[0].code}
                        </div>
                      </>
                    )}
                  </div>

                  {(cart.estimatedCost.totalAmount.amount > after_limit) && (
                    <div id={styles.afterpay}>
                      <afterpay-placement 
                        data-locale="en_US"
                        data-currency="USD" 
                        data-amount={cart.estimatedCost.totalAmount.amount}
                        data-modal-theme="white"
                        data-logo-type="lockup"
                      >
                        {process.env.NODE_ENV !== 'production' && (
                          <PlaceholdAfterp />
                        )}
                      </afterpay-placement>
                    </div>
                  )}
                  <a className={solid_btn} href={cart.checkout}>
                    Checkout Est Total
                    <span className={styles.cta_spaceprice}>{price_format(cart.estimatedCost.checkoutChargeAmount.amount)}</span>
                  </a>
                </>
              )}
            </div>
          </>
        ) : (
          <CartEmpty />
        )}

        
        {cartChanging && (
          <div className={`${styles.changing} ${cartError ? styles.errorbg : ''}`}>
            {cartError ? (
              <div className={styles.error}>
                <div className={styles.title}>
                  Unable to add to cart
                </div>
                <ul>
                  {cartError.map( (item,index) => 
                    <li key={`error-${index}`}>
                      {item}
                    </li>
                  )}
                </ul>
              </div>
            ) : (
              <div>Updating Cart</div>
            )}
          </div>
        )}
      </aside>
    </>
  )
};


const CartEmpty = () => {
  const { setFreeze, setCartClose } = useTheme()

  const handleClose = () => {
    setCartClose(true)
    setFreeze(false)
  }

  return (
    <div className={styles.empty}>
      <div className={styles.message}>
        Your cart is empty
        <div className={styles.subtitle}>
          Let's change that
        </div>
      </div>
      <div className={styles.collections}>
        <Flink className={styles.coll} href="/collections/best-sellers" onClick={(e)=> {handleClose()}}>
          <div className={styles.image}>
            <img 
              className={styles.img} 
              src={imgSize('https://cdn.shopify.com/s/files/1/0123/5065/2473/files/Mens_EmptyCart.jpg?v=1723672654',400)} 
              width="300" 
              height="400" 
              loading="lazy" 
              decode="async" 
              alt=""
            />
          </div>
          Men's Best Sellers
        </Flink>
        <Flink className={styles.coll} href="/collections/womens-best-sellers" onClick={(e)=> {handleClose()}}>
          <div className={styles.image}>
            <img 
              className={styles.img} 
              src={imgSize('https://cdn.shopify.com/s/files/1/0123/5065/2473/files/Womens_EmptyCart-copy.jpg?v=1723672659',400)} 
              width="300" 
              height="400" 
              loading="lazy" 
              decode="async" 
              alt=""
            />
          </div>
          Women's Best Sellers
        </Flink>
      </div>
    </div>
  )
}



export default Cart



